// SPDX-License-Identifier: AGPL-3.0-or-later
// Hand-rolled SVG chart builders (return markup strings). No chart libraries.
import { num, tip } from './format';

export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

/** Tiny inline sparkline. */
export function spark(values: number[], opts: { w?: number; h?: number; colour?: string; fill?: boolean } = {}): string {
  const w = opts.w ?? 90;
  const h = opts.h ?? 24;
  const colour = opts.colour ?? 'var(--branch)';
  const vals = values.filter((v) => Number.isFinite(v));
  if (vals.length < 2) return `<svg class="spark" width="${w}" height="${h}" aria-hidden="true"></svg>`;
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (w - 2) + 1;
    const y = h - 2 - ((v - min) / span) * (h - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const area = opts.fill
    ? `<polygon points="${w - 1},${h - 1} 1,${h - 1} ${pts.join(' ')}" fill="${colour}" opacity="0.12" />`
    : '';
  return `<svg class="spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">${area}<polyline points="${pts.join(' ')}" fill="none" stroke="${colour}" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" /></svg>`;
}

interface AreaSeries { key: string; label: string; colour: string; values: number[] }

/**
 * Stacked area over categorical x (years). Order bottom→top follows `series`.
 * Each band segment carries a data-tip with the year and value.
 */
export function stackedArea(
  years: number[],
  series: AreaSeries[],
  opts: { w?: number; h?: number; unit?: string; benchmark?: { value: number; label: string } } = {},
): string {
  const w = opts.w ?? 720;
  const h = opts.h ?? 320;
  const m = { t: 14, r: 16, b: 28, l: 48 };
  const iw = w - m.l - m.r;
  const ih = h - m.t - m.b;
  const totals = years.map((_, i) => series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const yMax = niceMax(Math.max(...totals, opts.benchmark?.value ?? 0) * 1.02);
  const x = (i: number) => m.l + (years.length === 1 ? iw / 2 : (i / (years.length - 1)) * iw);
  const y = (v: number) => m.t + ih - (v / yMax) * ih;

  // cumulative baselines
  const base = years.map(() => 0);
  let bands = '';
  const dots: string[] = [];
  for (const s of series) {
    const top = years.map((_, i) => base[i] + (s.values[i] || 0));
    const upper = years.map((_, i) => `${x(i).toFixed(1)},${y(top[i]).toFixed(1)}`);
    const lower = years.map((_, i) => `${x(i).toFixed(1)},${y(base[i]).toFixed(1)}`).reverse();
    bands += `<polygon points="${upper.join(' ')} ${lower.join(' ')}" fill="${s.colour}" opacity="0.9" />`;
    years.forEach((yr, i) => {
      const cy = y(base[i] + (s.values[i] || 0) / 2);
      dots.push(
        `<rect x="${(x(i) - 8).toFixed(1)}" y="${(y(top[i])).toFixed(1)}" width="16" height="${Math.max(0, y(base[i]) - y(top[i])).toFixed(1)}" fill="transparent" data-tip="${tip(`${yr}: ${s.label} ${num(s.values[i])}`)}" />`,
      );
      void cy;
    });
    for (let i = 0; i < years.length; i++) base[i] = top[i];
  }

  // y grid + labels
  let grid = '';
  const ticks = 4;
  for (let t = 0; t <= ticks; t++) {
    const v = (yMax / ticks) * t;
    const yy = y(v);
    grid += `<line class="grid-line" x1="${m.l}" y1="${yy.toFixed(1)}" x2="${m.l + iw}" y2="${yy.toFixed(1)}" />`;
    grid += `<text class="axis-text" x="${m.l - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${num(v)}</text>`;
  }
  // x labels
  let xlab = '';
  years.forEach((yr, i) => {
    if (years.length > 8 && i % 2 === 1 && i !== years.length - 1) return;
    xlab += `<text class="axis-text" x="${x(i).toFixed(1)}" y="${(m.t + ih + 16).toFixed(1)}" text-anchor="middle">${yr}</text>`;
  });
  let bench = '';
  if (opts.benchmark) {
    const by = y(opts.benchmark.value);
    bench = `<line class="benchmark-line" x1="${m.l}" y1="${by.toFixed(1)}" x2="${m.l + iw}" y2="${by.toFixed(1)}" /><text class="annot-text" x="${m.l + iw}" y="${(by - 4).toFixed(1)}" text-anchor="end">${opts.benchmark.label}</text>`;
  }
  return `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="Stacked area chart">${grid}${bands}${bench}${dots.join('')}${xlab}</svg>`;
}

/** Multiple line series over years (e.g. per-100k access by remoteness). */
export function multiLine(
  years: number[],
  series: AreaSeries[],
  opts: { w?: number; h?: number; yMax?: number; unit?: string; fmt?: (v: number) => string } = {},
): string {
  const w = opts.w ?? 720;
  const h = opts.h ?? 320;
  const m = { t: 14, r: 74, b: 28, l: 48 };
  const iw = w - m.l - m.r;
  const ih = h - m.t - m.b;
  const fmt = opts.fmt ?? ((v: number) => num(v));
  const yMax = opts.yMax ?? niceMax(Math.max(...series.flatMap((s) => s.values.filter(Number.isFinite))) * 1.05);
  const x = (i: number) => m.l + (years.length === 1 ? iw / 2 : (i / (years.length - 1)) * iw);
  const y = (v: number) => m.t + ih - (v / yMax) * ih;
  let grid = '';
  const ticks = 4;
  for (let t = 0; t <= ticks; t++) {
    const v = (yMax / ticks) * t;
    const yy = y(v);
    grid += `<line class="grid-line" x1="${m.l}" y1="${yy.toFixed(1)}" x2="${m.l + iw}" y2="${yy.toFixed(1)}" />`;
    grid += `<text class="axis-text" x="${m.l - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${fmt(v)}</text>`;
  }
  let xlab = '';
  years.forEach((yr, i) => {
    if (years.length > 8 && i % 2 === 1 && i !== years.length - 1) return;
    xlab += `<text class="axis-text" x="${x(i).toFixed(1)}" y="${(m.t + ih + 16).toFixed(1)}" text-anchor="middle">${yr}</text>`;
  });
  let lines = '';
  let dots = '';
  let labels = '';
  for (const s of series) {
    const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    lines += `<polyline points="${pts.join(' ')}" fill="none" stroke="${s.colour}" stroke-width="2" stroke-linejoin="round" />`;
    s.values.forEach((v, i) => {
      dots += `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="6" fill="transparent" data-tip="${tip(`${years[i]}: ${s.label} ${fmt(v)}`)}" /><circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.4" fill="${s.colour}" />`;
    });
    const lastY = y(s.values[s.values.length - 1]);
    labels += `<text class="axis-text" x="${(m.l + iw + 5).toFixed(1)}" y="${(lastY + 3).toFixed(1)}" fill="${s.colour}" style="font-weight:600">${s.label}</text>`;
  }
  return `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="Line chart">${grid}${lines}${dots}${labels}${xlab}</svg>`;
}
